import { InteractiveZone, InteractiveZonesInfo } from "../types";
import { getAttributesCircle } from "./getAttributesCircle";
import { getAttributesCircleFromShape } from "./getAttributesCircleFromShape";
import { getResourceId } from "./getResourceId";

export function parseInteractiveZonesSvg(
  svg: SVGSVGElement,
  options?: { applyMatrixTransformToPosition?: boolean }
): InteractiveZonesInfo | null {
  const info: InteractiveZonesInfo = {
    mapInfo: null,
    zones: []
  };
  if (!svg) return null;
  const layer = svg.querySelector("g#resources");
  const defs = svg.querySelector("defs");

  const copyAttr = ["height", "width", "viewBox"];
  const attrs: Record<string, string> = {};
  for (const attr of copyAttr) {
    const v = svg.getAttribute(attr);
    if (v) {
      attrs[attr] = v;
    }
  }
  const bMap = svg.getBoundingClientRect();
  const { width: vbWidth, height: vbHeight } = svg.viewBox.baseVal;

  const centerX = vbWidth / 2;
  const centerY = vbHeight / 10;

  info.mapInfo = {
    attrs,
    defs: defs ? defs.innerHTML : "",
    width: bMap.width,
    height: bMap.height,
    vbWidth: vbWidth,
    vbHeight: vbHeight,
    startX: bMap.x,
    startY: bMap.y
  };

  // If there is no layer we can't parse the rest of SVG
  if (!layer) {
    return info;
  }

  // Grab every element that is a group
  const svgPaths: NodeListOf<SVGGElement> = layer.querySelectorAll("g");
  // Loop over each group element and create click zones for all
  // those that need it. This is the heaviest lifting piece on this page.
  svgPaths.forEach((value: SVGGElement, _: number) => {
    if (
      value.id === null ||
      value.id === "" ||
      value.classList.contains("js-map-resource-icon") ||
      value.classList.contains("js-map-resource-disabled")
    ) {
      return;
    }
    if (
      !value.id.startsWith("reso_") &&
      !value.id.startsWith("js-map-resource-icon-reso_")
    ) {
      return;
    }
    const id = getResourceId(value);
    if (id === null) {
      return;
    }

    const containerElement = value.closest('[id^="tactic_id_container_"]');
    const containerElementMapBuilder = value.closest(".spot-shape-container");

    // Client Transform Matrix is a little bit tricky -- basically you have to realize
    // getScreenCTM returns a DOM Matrix representing the matrix that transforms
    // the current element's coordinate system to the coordinate system of the SVG viewport.
    // by invoking inverts, we're doing the opposite SVG --> DOM Coordinates, thereby placing
    // the clickable element on the correct area of the map
    const clientTransformMatrix = value.getScreenCTM()?.inverse();
    if (clientTransformMatrix === null) {
      return;
    }

    // This needs to be the absolute value in order to set the width and height
    // prior to the transformation. If not, width and height are overwritten
    // with a width and height of auto
    const bounding = value.getBBox();

    // Returns the actual bounding box at the time the method is called, even if the element is not yet rendered
    // it does not account for any transformations applied to the element or its parents
    // Change #1 -- moving from getBBox to getBoundingClientRect -- because this is the true size of the element
    // since it's relative and continues to
    const actualResourceBox = value.getBoundingClientRect();
    // Create the singular point and place it at the top left x,y coordinates
    // of the element that we're covering with a click zone
    const point = svg.createSVGPoint();
    point.x = actualResourceBox.left;
    point.y = actualResourceBox.top;

    // We are created a new box, that's transparent & we are setting it's x and y position
    // based on the position of the original element prior to transforms
    const { x, y } = point.matrixTransform(clientTransformMatrix);
    // precalculate centering map coordinates
    const destX =
      ((x < centerX ? Math.abs(centerX - x) : -1 * Math.abs(x - centerX)) *
        bMap.width) /
      vbWidth;
    const destY =
      ((y < centerY ? Math.abs(centerY - y) : -1 * Math.abs(y - centerY)) *
        bMap.height) /
      vbHeight;
    const zone: InteractiveZone = {
      id,
      x,
      y,
      width: bounding.width,
      height: bounding.height,
      html: containerElement ? containerElement.outerHTML : value.innerHTML,
      containerTransformation: containerElement
        ? (containerElement.getAttribute("transform") ?? undefined)
        : "",
      mapBuilderContainerTransformation: containerElementMapBuilder
        ? (containerElementMapBuilder.getAttribute("transform") ?? undefined)
        : "",
      destX,
      destY
    };

    const circle: SVGCircleElement | null =
      value.querySelector("circle[stroke]");
    if (circle) {
      zone.circle = getAttributesCircle(circle, options);
    } else {
      // Some uploaded maps draw the dot as a round path, which would
      // otherwise leave the spot without an occupant avatar
      const shape: SVGGraphicsElement | null = value.querySelector(
        "path[stroke], ellipse[stroke]"
      );
      if (shape) {
        zone.circle = getAttributesCircleFromShape(shape);
      }
    }

    info.zones.push(zone);
  });
  return info;
}
